
import { toast } from 'sonner';
import { downloadPdfFromHtml } from '@/utils/downloadPdfFromHtml';
import { captureError } from '@/lib/sentry';

export interface PdfGenerationOptions {
  htmlContent: string;
  estimateData: any;
  filename?: string;
  type?: 'estimate' | 'invoice' | 'credit_note';
  action?: 'download' | 'preview';
  footerText?: string;
}

export const usePdfGeneration = () => {
  const generatePdf = async (options: PdfGenerationOptions) => {
    try {
      console.log(`[PDF Generation] Starting ${options.type} PDF generation`);
      
      const isDownload = options.action === 'download';
      
      // Get the footer text from options or estimate data
      const footerText = options.footerText || options.estimateData?.footerText || `Payment terms: ${options.estimateData?.invoice?.payment_terms || 'Net 30 days'}.`;
      
      const logoUrl = options.estimateData?.company?.logo_url || '';

      // Build @top-left block only if logo URL exists
      const topLeftBlock = logoUrl
        ? `@top-left {
                  content: url('${logoUrl}');
                  width: 200px;
                  height: auto;
                  margin-bottom: 30px;
                }`
        : '';

      // Build the full HTML document with @page CSS
      const printHtml = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="UTF-8">
            <title>${options.filename || `${options.type}-print`}</title>
            <style>
              @page {
                size: A4;
                margin: 1.5in 1in 1.2in 1in;
                
                ${topLeftBlock}
                
                @bottom-right {
                  content: "Page " counter(page) " of " counter(pages);
                  font-family: Arial, sans-serif;
                  font-size: 9px;
                }
                
                @bottom-left {
                  content: "${footerText.replace(/"/g, '\\"')}";
                  font-family: Arial, sans-serif;
                  font-size: 9px;
                  font-weight: bold;
                  font-style: italic;
                  width: 85%;
                }
              }
              
              body { 
                font-family: Arial, sans-serif; 
                margin: 0; 
                padding: 0; 
                font-size: 11px; 
                line-height: 1.15;
                color: #000;
                background: white;
              }
              
              .print-content {
                padding-top: 0 !important;
                padding-bottom: 0 !important;
              }
              
              .amount-due-section {
                border: 2px solid !important;
                border-radius: 8px !important;
                padding: 12px !important;
                background-color: #f9fafb !important;
                text-align: center !important;
                margin-bottom: 24px !important;
              }
              
              .amount-due-paid { border-color: #16a34a !important; }
              .amount-due-overdue { border-color: #dc2626 !important; }
              .amount-due-unpaid { border-color: #ea580c !important; }
              .amount-due-due { border-color: #2563eb !important; }
              .amount-due-draft { border-color: #6b7280 !important; }
              .amount-due-cancelled { border-color: #dc2626 !important; }
              
              .amount-due-section h3 { font-size: 16px !important; font-weight: bold !important; margin-bottom: 8px !important; color: #374151 !important; }
              .amount-due-section .text-xl { font-size: 20px !important; font-weight: bold !important; margin-bottom: 8px !important; }
              .amount-due-section .text-base { font-size: 16px !important; font-weight: 600 !important; color: #374151 !important; }
              .amount-due-section .text-sm { font-size: 14px !important; font-weight: normal !important; }
              .amount-due-section .text-xs { font-size: 12px !important; font-weight: normal !important; }
              .amount-due-section .max-w-xs { max-width: 200px !important; margin-left: auto !important; margin-right: auto !important; }
              .amount-due-section .flex { display: flex !important; }
              .amount-due-section .justify-between { justify-content: space-between !important; }
              .amount-due-section .items-center { align-items: center !important; }
              .amount-due-section .border-t-2 { border-top: 2px solid #9ca3af !important; margin-top: 8px !important; padding-top: 8px !important; }
              
              .no-print, .print-hide, button, .dialog-close, .modal-actions, .preview-actions, .print-actions {
                display: none !important;
              }
              
              @media print {
                body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                .no-print, .print-hide, button, .dialog-close, .modal-actions, .preview-actions, .print-actions { display: none !important; }
                .print-only { display: block !important; }
                .page-break-before { page-break-before: always; }
                .page-break-after { page-break-after: always; }
                .no-page-break { page-break-inside: avoid; break-inside: avoid; }
              }
              
              .print-only { display: none; }
              
              table { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
              th, td { border: 1pt solid #000000 !important; padding: 8px; text-align: left; }
              th { background-color: #f5f5f5; font-weight: bold; }
              
              .text-right { text-align: right; }
              .font-bold { font-weight: bold; }
              strong { font-weight: bold; }
              .text-lg { font-size: 16px; }
              .text-xl { font-size: 18px; }
              .text-2xl { font-size: 24px; }
              .mb-2 { margin-bottom: 8px; }
              .mb-4 { margin-bottom: 16px; }
              .mb-6 { margin-bottom: 24px; }
              .mb-8 { margin-bottom: 32px; }
              .mt-2 { margin-top: 8px; }
              .mt-4 { margin-top: 16px; }
              .mt-8 { margin-top: 32px; }
              .p-4 { padding: 16px; }
              .bg-blue-50 { background-color: #eff6ff; }
              .border { border: 1px solid #d1d5db; }
              .border-blue-200 { border-color: #bfdbfe; }
              .rounded { border-radius: 4px; }
              .text-blue-800 { color: #1e40af; }
              
              .bg-green-500 { background-color: #22c55e !important; }
              .bg-blue-500 { background-color: #3b82f6 !important; }
              .bg-gray-400 { background-color: #9ca3af !important; }
              .bg-gray-50 { background-color: #f9fafb !important; }
              .bg-gray-100 { background-color: #f3f4f6 !important; }
              .bg-gray-200 { background-color: #e5e7eb !important; }
              .text-green-600 { color: #16a34a !important; }
              .text-white { color: white !important; }
              .border-gray-300 { border-color: #d1d5db !important; }
              .border-gray-400 { border-color: #9ca3af !important; }
              .border-gray-800 { border-color: #1f2937 !important; }
              .text-gray-600 { color: #6b7280 !important; }
              .text-gray-700 { color: #374151 !important; }
              .text-gray-900 { color: #111827 !important; }
            </style>
          </head>
          <body>
            <div class="print-container">
              ${options.htmlContent}
            </div>
          </body>
        </html>
      `;

      if (isDownload) {
        // Server-side PDF generation + blob download (mobile-safe)
        const filename = options.filename || `${options.type || 'document'}-download`;
        await downloadPdfFromHtml(printHtml, filename);
        toast.success('PDF downloaded successfully');
      } else {
        // Preview mode — still open in new tab (user initiated, less likely blocked,
        // and preview is not the problematic flow)
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
          throw new Error('Unable to open preview window. Please check your popup blocker.');
        }

        const previewHtml = printHtml.replace(
          '</body>',
          `<div class="print-actions" style="position:fixed;top:20px;right:20px;background:white;padding:10px;border-radius:8px;box-shadow:0 2px 10px rgba(0,0,0,0.1);z-index:1000;">
            <button onclick="window.print()" style="display:inline-block!important;background:#3b82f6;color:white;border:none;padding:8px 16px;border-radius:4px;cursor:pointer;font-size:14px;">Print/Download PDF</button>
          </div></body>`
        );

        printWindow.document.write(previewHtml);
        printWindow.document.close();
        toast.success('Preview opened in new tab');
      }

      return true;

    } catch (error) {
      captureError(error, { feature: 'pdf-generation', action: options.action || 'unknown', type: options.type || 'unknown' });
      console.error('[PDF Generation] Error:', error);
      
      let errorMessage = 'Failed to generate PDF';
      if (error instanceof Error) {
        if (error.message.includes('popup blocker')) {
          errorMessage = 'Unable to open preview window - please disable popup blocker';
        } else {
          errorMessage = error.message;
        }
      }
      
      toast.error(errorMessage);
      return false;
    }
  };

  return { generatePdf };
};
